// Feature: features/super-admin-billing-and-user-activity.md
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  Input,
  Checkbox,
  Spinner,
  Alert,
  AlertIcon,
  Collapse,
  HStack,
  VStack,
  Divider
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalLinkIcon,
  RepeatIcon
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import { formatRelativeTime, formatTimestamp } from './SuperAdminOrgPulseView';

const CATEGORY_BADGE_CONFIG = {
  cases_viewed: { label: 'Case View', colorScheme: 'blue' },
  searches: { label: 'Search', colorScheme: 'purple' },
  reports_generated: { label: 'Report / PDF', colorScheme: 'teal' },
  files_downloaded: { label: 'File Download', colorScheme: 'cyan' },
  chat_questions: { label: 'AI Chat', colorScheme: 'orange' },
  dashboards_viewed: { label: 'Dashboard', colorScheme: 'gray' },
  feedback_or_admin: { label: 'Feedback / Admin', colorScheme: 'pink' },
  other: { label: 'Action', colorScheme: 'gray' }
};

const ORG_CASE_ROUTE_BY_ID = {
  1: '/ionm-strict-analysis/',
  155: '/usn-case/',
  203: '/mph-surgical-assist/'
};

const SuperAdminUserActivityPanel = ({ daysWindow = 14 }) => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();

  const [activityDays, setActivityDays] = useState(() => Math.max(daysWindow || 14, 14));
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [orgFilter, setOrgFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideUploadOnly, setHideUploadOnly] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState({});

  const fetchUserActivity = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);

    try {
      const response = await callApi(
        `/admin/super-dashboard/user-activity?days=${activityDays}`,
        accessToken,
        { method: 'GET' }
      );
      if (response.status === 403) {
        setError('Super-Admin permissions are required to view user activity.');
        return;
      }
      if (!response.ok) {
        throw new Error('Failed to load recent logins and user activity.');
      }
      const data = await response.json();
      setActivityData(data);
    } catch (err) {
      console.error('Error fetching Super-Admin user activity:', err);
      setError(err.message || 'Failed to load user activity.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, activityDays]);

  useEffect(() => {
    fetchUserActivity();
  }, [fetchUserActivity]);

  const toggleUserExpand = (userId) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleOpenCase = (orgId, caseId, externalCaseId) => {
    const prefix = ORG_CASE_ROUTE_BY_ID[orgId];
    if (!prefix) return;
    const target = orgId === 1 ? (externalCaseId || caseId) : (caseId || externalCaseId);
    if (target) {
      navigate(`${prefix}${target}`);
    }
  };

  const users = useMemo(() => activityData?.users || [], [activityData]);
  const recentFeed = useMemo(() => activityData?.recent_feed || [], [activityData]);
  const summary = activityData?.summary || {
    active_users: 0,
    interactive_users: 0,
    active_orgs_count: 0,
    cases_viewed: 0,
    searches: 0,
    reports_generated: 0,
    files_downloaded: 0,
    chat_questions: 0,
    dashboards_viewed: 0
  };

  const orgOptions = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      const key = u.org_id ? String(u.org_id) : 'UNASSIGNED';
      if (!map.has(key)) {
        map.set(key, u.org_name || `Org ${u.org_id}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (hideUploadOnly && u.is_upload_only) return false;
      if (orgFilter !== 'ALL' && String(u.org_id || 'UNASSIGNED') !== orgFilter) return false;
      if (!q) return true;

      const matchBasic =
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.org_name || '').toLowerCase().includes(q);
      if (matchBasic) return true;

      return (u.recent_actions || []).some(
        (act) =>
          (act.label || '').toLowerCase().includes(q) ||
          (act.detail || '').toLowerCase().includes(q) ||
          (act.external_case_id || '').toLowerCase().includes(q)
      );
    });
  }, [users, hideUploadOnly, orgFilter, searchQuery]);

  const filteredFeed = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return recentFeed.filter((item) => {
      if (orgFilter !== 'ALL' && String(item.org_id || 'UNASSIGNED') !== orgFilter) return false;
      if (!q) return true;
      return (
        (item.user_name || '').toLowerCase().includes(q) ||
        (item.email || '').toLowerCase().includes(q) ||
        (item.org_name || '').toLowerCase().includes(q) ||
        (item.label || '').toLowerCase().includes(q) ||
        (item.detail || '').toLowerCase().includes(q) ||
        (item.external_case_id || '').toLowerCase().includes(q)
      );
    });
  }, [recentFeed, orgFilter, searchQuery]);

  if (loading && !activityData) {
    return (
      <Flex justify="center" align="center" py={16}>
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box>
      {error && (
        <Alert status="error" borderRadius="md" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Top KPI Summary Strip */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Active Customer Users ({activityDays}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber fontSize="2xl" color="blue.600">
                  {summary.active_users}
                </StatNumber>
                <Badge colorScheme="purple" fontSize="xs">
                  {summary.active_orgs_count} Orgs
                </Badge>
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                {summary.interactive_users} interactive • excludes @revedy.io & bots
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Cases Viewed & Searched ({activityDays}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber fontSize="2xl" color="gray.800">
                  {summary.cases_viewed}
                </StatNumber>
                <Badge colorScheme="purple" fontSize="xs">
                  {summary.searches} searches
                </Badge>
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                Case detail & coding analysis views
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                Reports & File Downloads ({activityDays}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber fontSize="2xl" color="teal.600">
                  {summary.reports_generated}
                </StatNumber>
                <Badge colorScheme="cyan" fontSize="xs">
                  {summary.files_downloaded} downloads
                </Badge>
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                CMS-1500 PDFs, generated notes & case files
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
          <CardBody py={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase">
                AI Chat & Dashboards ({activityDays}d)
              </StatLabel>
              <HStack align="baseline" spacing={2}>
                <StatNumber fontSize="2xl" color="orange.500">
                  {summary.chat_questions}
                </StatNumber>
                <Badge colorScheme="gray" fontSize="xs">
                  {summary.dashboards_viewed} dashboard views
                </Badge>
              </HStack>
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                Interactive coding questions & compliance views
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Main Per-User Table Card */}
      <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white" mb={6}>
        <CardHeader pb={3}>
          <Flex
            justify="space-between"
            align={{ base: 'flex-start', lg: 'center' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={3}
          >
            <Box>
              <HStack spacing={2}>
                <Heading size="sm" color="gray.800">
                  All Recent Customer Logins & Activity Summary
                </Heading>
                <Badge colorScheme="blue">{filteredUsers.length} users</Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                Click any user row to expand their chronological activity log, searched cases, generated reports, and AI chat questions
              </Text>
            </Box>

            <HStack spacing={2.5} flexWrap="wrap">
              <Input
                size="xs"
                w="210px"
                bg="white"
                placeholder="Search user, org, case #, chat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Select
                size="xs"
                w="160px"
                bg="white"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
              >
                <option value="ALL">All Orgs ({orgOptions.length})</option>
                {orgOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
              <Select
                size="xs"
                w="130px"
                bg="white"
                value={activityDays}
                onChange={(e) => setActivityDays(Number(e.target.value))}
              >
                <option value={5}>Last 5 Days</option>
                <option value={7}>Last 7 Days</option>
                <option value={14}>Last 14 Days</option>
                <option value={30}>Last 30 Days</option>
                <option value={60}>Last 60 Days</option>
              </Select>
              <Checkbox
                size="sm"
                isChecked={hideUploadOnly}
                onChange={(e) => setHideUploadOnly(e.target.checked)}
              >
                <Text fontSize="xs" color="gray.600">
                  Hide upload-only
                </Text>
              </Checkbox>
              <Button
                size="xs"
                leftIcon={<RepeatIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={fetchUserActivity}
                isLoading={loading}
              >
                Refresh
              </Button>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody pt={0}>
          {filteredUsers.length === 0 ? (
            <Flex justify="center" align="center" py={10} color="gray.500">
              <Text fontSize="sm">No customer user logins match the selected filters.</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>User & Organization</Th>
                    <Th>Last Seen</Th>
                    <Th>Engagement</Th>
                    <Th>What They Did (Breakdown)</Th>
                    <Th>Most Recent Interactive Action</Th>
                    <Th isNumeric>Timeline</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map((u) => {
                    const isExpanded = Boolean(expandedUsers[u.user_id]);
                    const b = u.action_breakdown || {};
                    const latestAction = (u.recent_actions || [])[0];
                    const canExpand = (u.recent_actions || []).length > 0 || (u.top_functions || []).length > 0;

                    return (
                      <React.Fragment key={u.user_id}>
                        <Tr
                          _hover={{ bg: 'gray.50' }}
                          bg={isExpanded ? 'blue.50' : 'transparent'}
                          cursor={canExpand ? 'pointer' : 'default'}
                          onClick={() => canExpand && toggleUserExpand(u.user_id)}
                        >
                          <Td>
                            <HStack spacing={2}>
                              <Badge colorScheme="purple" fontSize="2xs">
                                {u.org_name}
                              </Badge>
                              <Text fontWeight="bold" fontSize="xs" color="gray.800">
                                {u.name}
                              </Text>
                              {u.role && u.role !== 'user' && (
                                <Badge colorScheme="gray" fontSize="2xs">
                                  {u.role}
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="2xs" color="gray.500">
                              {u.email}
                            </Text>
                          </Td>

                          <Td whiteSpace="nowrap">
                            <Text fontSize="xs" fontWeight="semibold" color="gray.800">
                              {formatTimestamp(u.last_active_at)}
                            </Text>
                            <Badge colorScheme="green" variant="subtle" fontSize="2xs" mt={0.5}>
                              {formatRelativeTime(u.last_active_at)}
                            </Badge>
                          </Td>

                          <Td>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                              {u.active_days} {u.active_days === 1 ? 'day' : 'days'} active
                            </Text>
                            <Text fontSize="2xs" color="gray.500">
                              {u.interactive_events} interactive • {u.total_events} total calls
                            </Text>
                          </Td>

                          <Td>
                            <HStack spacing={1.5} flexWrap="wrap">
                              {b.cases_viewed > 0 && (
                                <Badge colorScheme="blue" fontSize="2xs">
                                  {b.cases_viewed} Cases Viewed
                                </Badge>
                              )}
                              {b.searches > 0 && (
                                <Badge colorScheme="purple" fontSize="2xs">
                                  {b.searches} Searches
                                </Badge>
                              )}
                              {b.reports_generated > 0 && (
                                <Badge colorScheme="teal" fontSize="2xs">
                                  {b.reports_generated} Reports
                                </Badge>
                              )}
                              {b.files_downloaded > 0 && (
                                <Badge colorScheme="cyan" fontSize="2xs">
                                  {b.files_downloaded} Downloads
                                </Badge>
                              )}
                              {b.chat_questions > 0 && (
                                <Badge colorScheme="orange" fontSize="2xs">
                                  {b.chat_questions} AI Chat Qs
                                </Badge>
                              )}
                              {b.dashboards_viewed > 0 && (
                                <Badge colorScheme="gray" fontSize="2xs">
                                  {b.dashboards_viewed} Dashboards
                                </Badge>
                              )}
                              {b.feedback_or_admin > 0 && (
                                <Badge colorScheme="pink" fontSize="2xs">
                                  {b.feedback_or_admin} Feedback/Admin
                                </Badge>
                              )}
                              {b.uploads > 0 && (
                                <Badge colorScheme="yellow" variant="outline" fontSize="2xs">
                                  {b.uploads.toLocaleString()} Uploads
                                </Badge>
                              )}
                            </HStack>
                          </Td>

                          <Td maxW="300px">
                            {latestAction ? (
                              <Box>
                                <Text fontSize="xs" fontWeight="medium" color="gray.800" noOfLines={1}>
                                  {latestAction.label}
                                </Text>
                                {latestAction.detail && (
                                  <Text fontSize="2xs" color="gray.500" noOfLines={1}>
                                    {latestAction.detail}
                                    {latestAction.external_case_id
                                      ? ` (${latestAction.external_case_id})`
                                      : ''}
                                  </Text>
                                )}
                              </Box>
                            ) : u.is_upload_only ? (
                              <Text fontSize="2xs" color="gray.400">
                                Automated case/file uploads only
                              </Text>
                            ) : (
                              <Text fontSize="2xs" color="gray.400">
                                Session check / login
                              </Text>
                            )}
                          </Td>

                          <Td isNumeric>
                            {canExpand && (
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleUserExpand(u.user_id);
                                }}
                              >
                                {isExpanded ? 'Hide' : `Actions (${(u.recent_actions || []).length})`}
                              </Button>
                            )}
                          </Td>
                        </Tr>

                        {/* Expanded Per-User Action Timeline */}
                        <Tr>
                          <Td colSpan={6} p={0} borderBottom={isExpanded ? '1px solid' : 'none'} borderColor="gray.200">
                            <Collapse in={isExpanded} animateOpacity>
                              <Box bg="gray.50" p={4} borderLeft="4px solid" borderColor="blue.400">
                                <Flex justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                                  <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                    Recent Interactive Activity for {u.name} ({u.email})
                                  </Text>
                                  <HStack spacing={1.5} flexWrap="wrap">
                                    {(u.top_functions || []).map((tf) => (
                                      <Badge key={tf.function_call} colorScheme="gray" variant="subtle" fontSize="2xs">
                                        {tf.function_call}: {tf.count}
                                      </Badge>
                                    ))}
                                  </HStack>
                                </Flex>

                                {(!u.recent_actions || u.recent_actions.length === 0) ? (
                                  <Text fontSize="xs" color="gray.500">
                                    No interactive UI actions recorded in the recent event buffer (only file uploads or background checks).
                                  </Text>
                                ) : (
                                  <Box bg="white" borderRadius="md" border="1px solid" borderColor="gray.200" overflowX="auto">
                                    <Table size="sm" variant="simple">
                                      <Thead bg="gray.100">
                                        <Tr>
                                          <Th fontSize="2xs" py={1.5}>When</Th>
                                          <Th fontSize="2xs" py={1.5}>Category</Th>
                                          <Th fontSize="2xs" py={1.5}>What They Did</Th>
                                          <Th fontSize="2xs" py={1.5}>Details / Case / Query</Th>
                                          <Th fontSize="2xs" py={1.5} isNumeric>Link</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {u.recent_actions.map((act) => {
                                          const catCfg =
                                            CATEGORY_BADGE_CONFIG[act.category] || CATEGORY_BADGE_CONFIG.other;
                                          const canOpenCase =
                                            Boolean(ORG_CASE_ROUTE_BY_ID[u.org_id]) &&
                                            Boolean(act.case_id || act.external_case_id);

                                          return (
                                            <Tr key={act.id} _hover={{ bg: 'gray.50' }}>
                                              <Td fontSize="2xs" py={1.5} whiteSpace="nowrap" color="gray.600">
                                                {formatTimestamp(act.timestamp)}{' '}
                                                <Text as="span" color="gray.400">
                                                  ({formatRelativeTime(act.timestamp)})
                                                </Text>
                                              </Td>
                                              <Td py={1.5}>
                                                <Badge colorScheme={catCfg.colorScheme} fontSize="2xs">
                                                  {catCfg.label}
                                                </Badge>
                                              </Td>
                                              <Td fontSize="xs" py={1.5} fontWeight="medium" color="gray.800">
                                                {act.label}
                                              </Td>
                                              <Td fontSize="xs" py={1.5} color="gray.700">
                                                {act.detail || '—'}
                                                {act.external_case_id && !(act.detail || '').includes(act.external_case_id) && (
                                                  <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Ext ID: {act.external_case_id}
                                                  </Badge>
                                                )}
                                                {act.case_description && (
                                                  <Text as="span" fontSize="2xs" color="gray.500" ml={2}>
                                                    ({act.case_description})
                                                  </Text>
                                                )}
                                              </Td>
                                              <Td py={1.5} isNumeric>
                                                {canOpenCase && (
                                                  <Button
                                                    size="2xs"
                                                    colorScheme="blue"
                                                    variant="ghost"
                                                    rightIcon={<ExternalLinkIcon />}
                                                    onClick={() =>
                                                      handleOpenCase(
                                                        u.org_id,
                                                        act.case_id,
                                                        act.external_case_id
                                                      )
                                                    }
                                                  >
                                                    Open Case
                                                  </Button>
                                                )}
                                              </Td>
                                            </Tr>
                                          );
                                        })}
                                      </Tbody>
                                    </Table>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </Td>
                        </Tr>
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Chronological Cross-Org Activity Stream */}
      <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <Box>
              <HStack spacing={2}>
                <Heading size="sm" color="gray.800">
                  Recent Interactive Customer Activity Stream
                </Heading>
                <Badge colorScheme="purple">{filteredFeed.length} events</Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                Chronological feed of recent case views, searches, generated reports, downloads, and AI chat questions across all orgs
              </Text>
            </Box>
          </Flex>
        </CardHeader>

        <CardBody pt={2}>
          {filteredFeed.length === 0 ? (
            <Text fontSize="xs" color="gray.500" py={4} textAlign="center">
              No recent interactive events match the current filter.
            </Text>
          ) : (
            <Box overflowX="auto" maxH="460px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th fontSize="2xs">Time</Th>
                    <Th fontSize="2xs">Org</Th>
                    <Th fontSize="2xs">User</Th>
                    <Th fontSize="2xs">Category</Th>
                    <Th fontSize="2xs">Action</Th>
                    <Th fontSize="2xs">Details</Th>
                    <Th fontSize="2xs" isNumeric>Case</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredFeed.map((ev) => {
                    const catCfg = CATEGORY_BADGE_CONFIG[ev.category] || CATEGORY_BADGE_CONFIG.other;
                    const canOpenCase =
                      Boolean(ORG_CASE_ROUTE_BY_ID[ev.org_id]) &&
                      Boolean(ev.case_id || ev.external_case_id);

                    return (
                      <Tr key={`feed-${ev.id}`} _hover={{ bg: 'gray.50' }}>
                        <Td fontSize="2xs" whiteSpace="nowrap" color="gray.600">
                          {formatTimestamp(ev.timestamp)}{' '}
                          <Text as="span" color="gray.400">
                            ({formatRelativeTime(ev.timestamp)})
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="purple" fontSize="2xs">
                            {ev.org_name}
                          </Badge>
                        </Td>
                        <Td fontSize="xs">
                          <Text fontWeight="medium" color="gray.800" noOfLines={1}>
                            {ev.user_name}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={catCfg.colorScheme} fontSize="2xs">
                            {catCfg.label}
                          </Badge>
                        </Td>
                        <Td fontSize="xs" fontWeight="medium" color="gray.800">
                          {ev.label}
                        </Td>
                        <Td fontSize="xs" color="gray.700">
                          {ev.detail || '—'}
                          {ev.external_case_id && !(ev.detail || '').includes(ev.external_case_id) && (
                            <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                              Ext ID: {ev.external_case_id}
                            </Badge>
                          )}
                        </Td>
                        <Td isNumeric>
                          {canOpenCase && (
                            <Button
                              size="2xs"
                              colorScheme="blue"
                              variant="ghost"
                              rightIcon={<ExternalLinkIcon />}
                              onClick={() =>
                                handleOpenCase(ev.org_id, ev.case_id, ev.external_case_id)
                              }
                            >
                              Open
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default SuperAdminUserActivityPanel;
